/**
 * The arithmetic every chart in this folder shares: where a value lands, what the axis says, and
 * which color a series wears.
 *
 * Pure functions in their own file so the geometry can be tested without rendering anything, and so
 * that two charts drawn side by side cannot disagree about where "nought" is.
 */

/** One line, one set of bars, or one layer of a stack. */
export interface Series {
  key: string
  label: string
  values: number[]
}

export interface Plot {
  width: number
  height: number
  padding: { top: number; right: number; bottom: number; left: number }
}

/** Room enough for four-figure axis labels on the left and a row of dates underneath. */
export const PLOT: Plot = {
  width: 720,
  height: 260,
  padding: { top: 12, right: 16, bottom: 30, left: 52 }
}

export const inner = (plot: Plot) => ({
  left: plot.padding.left,
  top: plot.padding.top,
  right: plot.width - plot.padding.right,
  bottom: plot.height - plot.padding.bottom,
  width: plot.width - plot.padding.left - plot.padding.right,
  height: plot.height - plot.padding.top - plot.padding.bottom
})

/**
 * The series color for a slot.
 *
 * Assigned by the series' fixed position in the chart's own list and never by its rank, so hiding one
 * series does not repaint the others — a chart whose colors move when it is filtered is one nobody
 * can read twice. The ramp does not cycle: a seventh series would silently reuse the first color, so
 * callers fold the tail into one "other" series instead.
 */
export const SERIES_SLOTS = 6

export function seriesColor(index: number): string {
  return `var(--chart-${Math.min(index, SERIES_SLOTS - 1) + 1})`
}

/**
 * Axis ticks on round numbers.
 *
 * Rounded up to a 1, 2 or 5 times a power of ten so the labels read 0 / 500 / 1,000 rather than
 * 0 / 437 / 874. The top tick is at or above the largest value, which is what keeps the tallest mark
 * inside the plot instead of touching its ceiling.
 */
export function niceTicks(maximum: number, count = 4): number[] {
  if (!Number.isFinite(maximum) || maximum <= 0) return [0, 1]

  const rough = maximum / count
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const step = [1, 2, 5, 10].map((factor) => factor * magnitude).find((candidate) => candidate >= rough) ?? magnitude * 10

  const ticks: number[] = []
  for (let value = 0; value < maximum + step; value += step) ticks.push(Number(value.toFixed(10)))
  return ticks
}

/** The value at the top of the axis: the highest tick, so a mark never runs off the top. */
export function ceiling(maximum: number, count = 4): number {
  const ticks = niceTicks(maximum, count)
  return ticks[ticks.length - 1] ?? 1
}

/** Where a band's center sits, given how many bands share the width. */
export function bandCenter(index: number, count: number, from: number, width: number): number {
  return count <= 0 ? from : from + (width * (index + 0.5)) / count
}

const COMPACT = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 })
const FULL = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 })

/** Axis labels, where space is short. */
export const axisNumber = (value: number): string => (value >= 10_000 ? COMPACT.format(value) : FULL.format(value))

/** Tooltips and the table, where the exact figure is the point. */
export const exactNumber = (value: number): string => FULL.format(value)

/**
 * A day label short enough for an axis.
 *
 * The rows carry ISO dates. Rendering them whole would overlap after about eight of them, and a
 * fourteen-day window is the default.
 */
export function shortDay(day: string): string {
  const parsed = new Date(day)
  if (Number.isNaN(parsed.getTime())) return day
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Which category labels to draw, so they never collide.
 *
 * Every other one, or every third, depending on how many there are and how wide the plot is. Drawing
 * all of them and letting the browser overlap is the most common way a time axis becomes unreadable.
 */
export function labelStride(count: number, width: number, labelWidth = 56): number {
  if (count <= 1) return 1
  return Math.max(1, Math.ceil(count / Math.max(1, Math.floor(width / labelWidth))))
}
